// ---------------------------------------------------------------------------
// The console frame (R12d)
//
// The system's frame around every signed-in page (REDESIGN-PLAN.md, section
// 4): the SideNav from 768 px, the MobileNav below, the skip link first, and
// main#main-content, the only scroller, holding the page. It replaces today's
// top bar for every account at once, with no flag (decision 7.15). Built from
// the ports (design/nav.ts, design/mobile-nav.ts) and Tailwind for the layout
// only, on the system's tokens; no daisyUI in its markup.
//
// In order:
//   - the skip link "Skip to main content": the first stop, also on a phone
//     (e2: before Menu); a primary Button as a link, shown while it has focus
//     by its host (the port's own class beats a utility on its element). It
//     focuses the page's h1, else main, giving the target tabindex="-1"
//     first: since c2 nothing has on the first load, and a browser does not
//     focus a bare h1 (found in Chrome, R12c). The router leaves it alone: it
//     is prevented and a fragment of this page (B3);
//   - the SideNav in a box shown from 768 px, which scrolls on its own, so
//     a landscape phone (844 × 390) can reach the footer; the custom element
//     has no box of its own (noBox), so the classes sit on its host div;
//   - the MobileNav below 768 px: Menu, "SDR Agent by NDI", and "+" (New
//     campaign), except on the new campaign itself (e4). The drawer is the
//     frame's to open and close, so Sign out and the legal links can close it
//     before their dialog opens: the old dialogs hang on <body>, and the
//     drawer's Tab trap would pull focus back out of them. It also closes
//     on every page shown and when the window grows to the SideNav's width;
//   - main, holding the page's box: up to --content-max wide, no padding
//     (the old pages bring their own). An old page's box is the legacy island
//     (.ndi-legacy, R12b2: 16px / 1.5 until it is rebuilt); the frame is
//     outside it, on the system's 14px / 22px.
// Root: a flex column at the height of initApp's fixed #root (h-full, not
// h-screen, which is taller than the visible area on iOS), a grid of the
// nav's width and the rest from 768 px. main is the only thing that scrolls.
//
// Both navs get the same items, the current page (nav-model.ts, d3) and the
// same footer (nav-footer.ts), but for its theme Select's id: one per nav
// (R12e), so each label "Theme" names its own. What the frame keeps itself
// — the theme shown, the drawer, the sign-out run — are signals bound with
// watch() (watch.ts), so they redraw also where Adornis's guard drew it.
// A nav's `value` is null, not undefined, on pages with no current item: an
// undefined value makes the SideNav uncontrolled, and it would keep showing
// the last item clicked.
//
// Signing out (B12): the question first, unchanged (title "Sign out", "Sign
// out of SDR Agent?", "Sign Out" in the primary style, e1), then the page's
// leave hooks — the wizard saves its draft, and may ask "Save draft?" (e3) —
// then the injected signOut. Cancel touches nothing; a hook that says no (or
// fails) keeps the person signed in, as it keeps them on the page. The
// button is busy while the hooks run.
// ---------------------------------------------------------------------------

import { computed, effect, signal } from '@preact/signals-core';
import { html, type TemplateResult } from 'lit';

import { confirmDialog } from '../confirm-dialog.js';
import '../design/mobile-nav.js';
import type { SdrMobileNav } from '../design/mobile-nav.js';
import '../design/nav.js';
import { button } from '../design/button.js';
import { showImprint, showPrivacyPolicy } from '../legal-dialog.js';
import { applyThemeChoice, saveThemeChoice, type ThemeChoice, themeStorage } from '../theme-choice.js';
import { type LegalText, navFooter } from './nav-footer.js';
import { BRAND, currentNavItem, NAV_ITEMS, type NavTarget, showsNewCampaign } from './nav-model.js';
import { watch } from './watch.js';

/** What the frame needs of the router. */
export interface FrameNav {
  goTo(name: NavTarget | 'newCampaign'): unknown;
  /** Runs the page's leave hooks; false when one said no. */
  runLeaveHooks(): Promise<boolean>;
  /** Calls the listener on every page shown; the drawer closes then. */
  onCommit(listener: () => void): () => void;
}

/** Where the SideNav takes over from the MobileNav: Tailwind's md, as the frame's md:block and md:hidden. */
const SIDE_NAV_FROM = '(min-width: 48rem)';

export interface AppFrameOptions {
  nav: FrameNav;
  /** Signs out once the person said yes and the leave hooks let them go (the app: Adornis's logout until R13). */
  signOut: () => unknown;
}

export interface FrameInput {
  /** The current route's name. */
  route: string;
  /** The page, or the frame's own view. */
  content: unknown;
  /** True for an old page: its box is the legacy island. */
  island: boolean;
}

export interface AppFrame {
  /** The theme both footers show; startup seeds it with the choice it applied. */
  readonly theme: { value: ThemeChoice };
  render(input: FrameInput): TemplateResult;
}

/** The skip link's target: the page's h1, else main. */
function skipToMain(event: Event) {
  event.preventDefault();
  const main = document.getElementById('main-content');
  const target = main?.querySelector<HTMLElement>('h1') ?? main;
  if (!target) return;
  if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
  target.focus();
}

export function createAppFrame(options: AppFrameOptions): AppFrame {
  const theme = signal<ThemeChoice>('system');
  const drawerOpen = signal(false);
  const signingOut = signal(false);

  // The drawer belongs to the page it was opened on and to a phone's width (review, both measured in
  // Chrome). A page change from outside it — Back, Android's back key — closes it: left open, it stayed
  // modal over the new page while the page change put focus on the h1 behind it. So does a window that
  // grows to the SideNav's width while it is open: CSS hides the MobileNav there, and its Tab trap,
  // with nothing visible in it, swallowed every Tab on the desktop page. The listener is there only
  // while the drawer is open.
  options.nav.onCommit(() => {
    drawerOpen.value = false;
  });
  effect(() => {
    if (!drawerOpen.value || typeof matchMedia !== 'function') return;
    const wide = matchMedia(SIDE_NAV_FROM);
    const close = (event: MediaQueryListEvent) => {
      if (event.matches) drawerOpen.value = false;
    };
    wide.addEventListener('change', close);
    return () => wide.removeEventListener('change', close);
  });

  const pickTheme = (choice: ThemeChoice) => {
    applyThemeChoice(choice, document.documentElement);
    // Blocked storage is no error: the choice then lasts for this page, and both footers still show it.
    saveThemeChoice(choice, themeStorage());
    theme.value = choice;
  };

  /** When `from` is in the open drawer: closes it and waits until it is closed and focus is back on Menu. */
  async function leaveDrawer(from: EventTarget | null) {
    const nav = from instanceof Element ? from.closest<SdrMobileNav>('sdr-mobile-nav') : null;
    if (!nav || !drawerOpen.peek()) return;
    drawerOpen.value = false;
    await nav.updateComplete;
  }

  async function signOutFlow(event: MouseEvent) {
    if (signingOut.peek()) return;
    await leaveDrawer(event.currentTarget);
    const yes = await confirmDialog('Sign out of SDR Agent?', {
      title: 'Sign out',
      confirmLabel: 'Sign Out',
      confirmClass: 'btn-primary',
    });
    if (!yes) return;
    signingOut.value = true;
    try {
      if (!(await options.nav.runLeaveHooks())) return;
      await options.signOut();
    } catch (error) {
      console.error('[frame] signing out failed', error);
    } finally {
      signingOut.value = false;
    }
  }

  async function openLegal(text: LegalText, event: MouseEvent) {
    await leaveDrawer(event.currentTarget);
    await (text === 'privacy' ? showPrivacyPolicy() : showImprint());
  }

  // One footer per nav, the same but for its theme Select's id (nav-footer.ts): one id in both would make the
  // drawer's label "Theme" name the side nav's Select.
  const footerFor = (themeId: string) =>
    computed(() =>
      navFooter({
        themeId,
        theme: theme.value,
        onTheme: pickTheme,
        signingOut: signingOut.value,
        onSignOut: event => void signOutFlow(event),
        onLegal: (text, event) => void openLegal(text, event),
      }),
    );
  const sideFooter = footerFor('nav-theme');
  const drawerFooter = footerFor('drawer-theme');

  const skipLink = button({ variant: 'primary', href: '#main-content', onClick: skipToMain }, 'Skip to main content');
  const go = (id: string) => void options.nav.goTo(id as NavTarget);
  const newCampaign = button({
    variant: 'primary',
    size: 'sm',
    icon: 'plus',
    ariaLabel: 'New campaign',
    onClick: () => void options.nav.goTo('newCampaign'),
  });

  return {
    theme,
    render({ route, content, island }) {
      const current = currentNavItem(route) ?? null;
      return html`<div
        class="flex h-full flex-col md:grid md:grid-cols-[var(--nav-width)_minmax(0,1fr)] md:grid-rows-[minmax(0,1fr)]"
      >
        <div class="sr-only focus-within:not-sr-only focus-within:absolute focus-within:top-2 focus-within:left-2 focus-within:z-(--z-toast)">
          ${skipLink}
        </div>
        <div class="hidden h-full overflow-y-auto md:block">
          <sdr-side-nav
            .items=${NAV_ITEMS}
            .product=${BRAND.product}
            .endorsement=${BRAND.endorsement}
            .endorsementMark=${BRAND.endorsementMark}
            .value=${current}
            .onChange=${go}
            .footer=${watch(sideFooter)}
          ></sdr-side-nav>
        </div>
        <div class="md:hidden">
          <sdr-mobile-nav
            .items=${NAV_ITEMS}
            .product=${BRAND.product}
            .endorsement=${BRAND.endorsement}
            .endorsementMark=${BRAND.endorsementMark}
            .value=${current}
            .onChange=${go}
            .footer=${watch(drawerFooter)}
            .actions=${showsNewCampaign(route) ? newCampaign : null}
            .open=${watch(drawerOpen)}
            .onOpenChange=${(open: boolean) => {
              drawerOpen.value = open;
            }}
          ></sdr-mobile-nav>
        </div>
        <main id="main-content" tabindex="-1" class="min-h-0 flex-1 overflow-auto outline-none">
          <div
            class=${island ? 'ndi-legacy mx-auto w-full max-w-(--content-max)' : 'mx-auto w-full max-w-(--content-max)'}
          >
            ${content}
          </div>
        </main>
      </div>`;
    },
  };
}
